import {
    Box,
    Grid,
    Skeleton,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import { useEncounter } from "../encounter.provider";
import { Controller } from "react-hook-form";
import dayjs from "dayjs";
import { useMemo } from "react";
import camelToCapitalizeText from "utils/camelToCapitalize";
import { isEmpty, isNil } from "lodash";
import buddhistEra from "dayjs/plugin/buddhistEra";
import calculateAgeDay from "utils/calculateAgeDay";
dayjs.extend(buddhistEra);
const PatientInfo = () => {
    const { encounterForm, loading, isEditable, rawEncounterData, recorder } =
        useEncounter();

    const { control, watch } = encounterForm;

    const watchResult = watch([
        "hospitalNumber",
        "transactionNumber",
        "service",
        "serviceName",
        "birthday",
        "sex",
        "firstName",
        "lastName",
        "doctorId",
        "doctorName"
    ]);

    const [
        hospitalNumber,
        transactionNumber,
        service,
        serviceName,
        birthday,
        sex,
        patientFName,
        patientLName,
        doctorId,
        doctorName
    ] = watchResult;

    const age = useMemo(() => {
        const { age, ageMonth, ageDay } = calculateAgeDay(
            dayjs(birthday).toDate(),
            dayjs(rawEncounterData?.createdAt)
        );
        return `${age} Y ${ageMonth} M ${ageDay} D`;
    }, [birthday, rawEncounterData?.createdAt]);

    if (loading) {
        return (
            <Stack spacing={2}>
                <Skeleton width="100%" height={40} />

                <Skeleton width="100%" height={40} />

                <Skeleton width="100%" height={40} />
            </Stack>
        );
    }

    if (isEditable) {
        return (
            <Grid container>
                <Grid item xs={12} sm={4} md={3} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        HN:
                    </Typography>
                    <Typography>{hospitalNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        TXN:
                    </Typography>
                    <Typography>{transactionNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        Patient Name:
                    </Typography>
                    <Typography>{`${patientFName} ${patientLName}`}</Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        Sex:
                    </Typography>
                    <Typography>
                        {camelToCapitalizeText(sex ?? "notRecord")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        Date Of Birth:
                    </Typography>
                    <Typography noWrap>
                        {dayjs(birthday).format("DD/MM/BBBB")}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        Age:
                    </Typography>
                    <Typography noWrap>{age}</Typography>
                </Grid>

                <Grid item xs={12} sm={4} md={3} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        Service:
                    </Typography>
                    <Typography noWrap>
                        {serviceName ?? "-"} {service ? `(${service})` : ""}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={6} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        Attending Doctor:
                    </Typography>
                    <Typography>
                        {isNil(doctorId) ? "-" : `${doctorName} (${doctorId})`}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={6} display={"flex"}>
                    <Typography sx={{ fontWeight: "bold" }} mr={1}>
                        Editing Doctor:
                    </Typography>
                    <Typography>
                        {isNil(recorder)
                            ? "-"
                            : `${
                                  isEmpty(recorder.name)
                                      ? "ไม่พบชื่อผู้แก้ไข"
                                      : recorder.name
                              } (${recorder.license})`}
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    return (
        <Stack spacing={2}>
            <Controller
                name="hospitalNumber"
                control={control}
                render={({ field }) => (
                    <Box sx={{ with: "100%", pt: 1 }}>
                        <TextField
                            {...field}
                            fullWidth
                            label="Hospital Number (HN)"
                            autoComplete="off"
                        />
                    </Box>
                )}
            />

            <Controller
                name="transactionNumber"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        label="Transaction Number (TXN)"
                        autoComplete="off"
                    />
                )}
            />

            <Controller
                name="service"
                control={control}
                render={({ field }) => {
                    return (
                        <TextField
                            {...field}
                            fullWidth
                            label="Service"
                            autoComplete="off"
                        />
                    );
                }}
            />
        </Stack>
    );
};

export default PatientInfo;
